import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import api from '../../config/api';

const CorporateRegistrationScreen = ({ navigation }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    companyId: '',
    billingAddress: '',
    billingEmail: '',
    taxId: '',
    creditLimit: '10000',
    billingPeriod: 'MONTHLY',
    adminName: '',
    adminEmail: '',
    adminPhone: '',
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.companyName.trim()) {
      Alert.alert('Error', 'Company name is required');
      return false;
    }
    if (!formData.companyId.trim()) {
      Alert.alert('Error', 'Company ID is required');
      return false;
    }
    if (!formData.billingEmail.trim()) {
      Alert.alert('Error', 'Billing email is required');
      return false;
    }
    if (!formData.adminEmail.trim() || !formData.adminEmail.includes('@')) {
      Alert.alert('Error', 'Valid admin email is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const response = await api.post('/corporate/create-account', {
        companyName: formData.companyName,
        companyId: formData.companyId,
        billingAddress: formData.billingAddress,
        billingEmail: formData.billingEmail,
        taxId: formData.taxId,
        billingPeriod: formData.billingPeriod,
        creditLimit: parseFloat(formData.creditLimit),
      });

      if (response.data.success) {
        Alert.alert(
          'Success!',
          'Corporate account created successfully. You can now manage employees and book corporate rides.',
          [
            {
              text: 'OK',
              onPress: () => navigation.goBack(),
            },
          ]
        );
      }
    } catch (error) {
      console.error('Registration error:', error);
      Alert.alert(
        'Error',
        error.response?.data?.message || 'Failed to create corporate account'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <LinearGradient
        colors={['#0a0a0a', '#1a1a2e', '#16213e']}
        style={styles.backgroundGradient}
      >
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Corporate Account</Text>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Hero Section */}
          <View style={styles.heroSection}>
            <View style={styles.iconContainer}>
              <Ionicons name="briefcase" size={48} color="#D4AF37" />
            </View>
            <Text style={styles.heroTitle}>Register Your Company</Text>
            <Text style={styles.heroSubtitle}>
              Create a corporate account to manage employee transportation and receive monthly invoicing
            </Text>
          </View>

          {/* Form */}
          <View style={styles.formCard}>
            <LinearGradient
              colors={['#1e1e1e', '#2d2d2d']}
              style={styles.formGradient}
            >
              {/* Company Information */}
              <Text style={styles.sectionTitle}>Company Information</Text>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Company Name *</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Acme Corporation"
                  placeholderTextColor="#666"
                  value={formData.companyName}
                  onChangeText={(text) => updateField('companyName', text)}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Company ID *</Text>
                <TextInput
                  style={styles.input}
                  placeholder="ACME001"
                  placeholderTextColor="#666"
                  value={formData.companyId}
                  onChangeText={(text) => updateField('companyId', text.toUpperCase())}
                  autoCapitalize="characters"
                />
                <Text style={styles.helpText}>Unique identifier for your company</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Billing Address</Text>
                <TextInput
                  style={[styles.input, styles.textArea]}
                  placeholder="1234 Business Ave, Washington, DC 20001"
                  placeholderTextColor="#666"
                  value={formData.billingAddress}
                  onChangeText={(text) => updateField('billingAddress', text)}
                  multiline
                  numberOfLines={3}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Billing Email *</Text>
                <TextInput
                  style={styles.input}
                  placeholder="billing@company.com"
                  placeholderTextColor="#666"
                  value={formData.billingEmail}
                  onChangeText={(text) => updateField('billingEmail', text)}
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Tax ID / EIN</Text>
                <TextInput
                  style={styles.input}
                  placeholder="12-3456789"
                  placeholderTextColor="#666"
                  value={formData.taxId}
                  onChangeText={(text) => updateField('taxId', text)}
                />
              </View>

              {/* Billing Settings */}
              <Text style={[styles.sectionTitle, { marginTop: 24 }]}>Billing Settings</Text>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Billing Period</Text>
                <View style={styles.segmentedControl}>
                  <TouchableOpacity
                    style={[
                      styles.segmentButton,
                      formData.billingPeriod === 'MONTHLY' && styles.segmentButtonActive,
                    ]}
                    onPress={() => updateField('billingPeriod', 'MONTHLY')}
                  >
                    <Text
                      style={[
                        styles.segmentButtonText,
                        formData.billingPeriod === 'MONTHLY' && styles.segmentButtonTextActive,
                      ]}
                    >
                      Monthly
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.segmentButton,
                      formData.billingPeriod === 'QUARTERLY' && styles.segmentButtonActive,
                    ]}
                    onPress={() => updateField('billingPeriod', 'QUARTERLY')}
                  >
                    <Text
                      style={[
                        styles.segmentButtonText,
                        formData.billingPeriod === 'QUARTERLY' && styles.segmentButtonTextActive,
                      ]}
                    >
                      Quarterly
                    </Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.segmentButton,
                      formData.billingPeriod === 'ANNUAL' && styles.segmentButtonActive,
                    ]}
                    onPress={() => updateField('billingPeriod', 'ANNUAL')}
                  >
                    <Text
                      style={[
                        styles.segmentButtonText,
                        formData.billingPeriod === 'ANNUAL' && styles.segmentButtonTextActive,
                      ]}
                    >
                      Annual
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Credit Limit (USD)</Text>
                <TextInput
                  style={styles.input}
                  placeholder="10000"
                  placeholderTextColor="#666"
                  value={formData.creditLimit}
                  onChangeText={(text) => updateField('creditLimit', text)}
                  keyboardType="numeric"
                />
                <Text style={styles.helpText}>Maximum monthly credit limit</Text>
              </View>

              {/* Benefits */}
              <View style={styles.benefitsSection}>
                <Text style={styles.benefitsTitle}>Corporate Account Benefits</Text>
                <View style={styles.benefitItem}>
                  <Ionicons name="checkmark-circle" size={20} color="#4CAF50" />
                  <Text style={styles.benefitText}>Monthly consolidated invoicing</Text>
                </View>
                <View style={styles.benefitItem}>
                  <Ionicons name="checkmark-circle" size={20} color="#4CAF50" />
                  <Text style={styles.benefitText}>Employee management dashboard</Text>
                </View>
                <View style={styles.benefitItem}>
                  <Ionicons name="checkmark-circle" size={20} color="#4CAF50" />
                  <Text style={styles.benefitText}>Detailed ride history & reports</Text>
                </View>
                <View style={styles.benefitItem}>
                  <Ionicons name="checkmark-circle" size={20} color="#4CAF50" />
                  <Text style={styles.benefitText}>Priority customer support</Text>
                </View>
                <View style={styles.benefitItem}>
                  <Ionicons name="checkmark-circle" size={20} color="#4CAF50" />
                  <Text style={styles.benefitText}>Flexible payment terms</Text>
                </View>
              </View>
            </LinearGradient>
          </View>

          {/* Submit Button */}
          <TouchableOpacity
            style={styles.submitButton}
            onPress={handleSubmit}
            disabled={loading}
          >
            <LinearGradient
              colors={['#D4AF37', '#B8860B']}
              style={styles.submitGradient}
            >
              {loading ? (
                <ActivityIndicator color="#000" />
              ) : (
                <>
                  <Ionicons name="checkmark-circle" size={24} color="#000" />
                  <Text style={styles.submitButtonText}>Create Corporate Account</Text>
                </>
              )}
            </LinearGradient>
          </TouchableOpacity>

          <View style={{ height: 30 }} />
        </ScrollView>
      </LinearGradient>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0a0a0a',
  },
  backgroundGradient: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 50,
    paddingBottom: 20,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#fff',
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
  },
  heroSection: {
    alignItems: 'center',
    marginBottom: 32,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: 'rgba(212,175,55,0.2)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  heroTitle: {
    fontSize: 28,
    fontWeight: '800',
    color: '#fff',
    marginBottom: 8,
  },
  heroSubtitle: {
    fontSize: 14,
    color: '#aaa',
    textAlign: 'center',
    lineHeight: 20,
    paddingHorizontal: 20,
  },
  formCard: {
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 20,
  },
  formGradient: {
    padding: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#D4AF37',
    marginBottom: 16,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 8,
  },
  input: {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 12,
    padding: 14,
    fontSize: 16,
    color: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(212,175,55,0.3)',
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  helpText: {
    fontSize: 12,
    color: '#888',
    marginTop: 6,
  },
  segmentedControl: {
    flexDirection: 'row',
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: 12,
    padding: 4,
  },
  segmentButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 8,
  },
  segmentButtonActive: {
    backgroundColor: '#D4AF37',
  },
  segmentButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#888',
  },
  segmentButtonTextActive: {
    color: '#000',
  },
  benefitsSection: {
    marginTop: 24,
    padding: 16,
    backgroundColor: 'rgba(212,175,55,0.1)',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(212,175,55,0.3)',
  },
  benefitsTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#D4AF37',
    marginBottom: 12,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  benefitText: {
    fontSize: 14,
    color: '#fff',
    marginLeft: 10,
  },
  submitButton: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  submitGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 18,
    gap: 12,
  },
  submitButtonText: {
    fontSize: 18,
    fontWeight: '800',
    color: '#000',
    letterSpacing: 1,
  },
});

export default CorporateRegistrationScreen;

